// One mouse drag of a product: where each move of the pointer puts it. Every
// product follows the same rules (CO-5846): it follows the pointer at its own
// height, or along one axis while Ctrl is held, snaps with nothing kept
// between moves, lines its faces up with nearby products and the room's
// walls across a gap, and stays in the room and above the floor.
import * as THREE from 'three';
import {CabinetMesh} from 'components/customer/RoomPlanner/types';
import {
    findSnaps,
    Snap,
} from 'components/customer/RoomPlanner/helpers/snapping/snaps';
import {HeldCorner} from 'components/customer/RoomPlanner/helpers/snapping/strategies/cornerSnap';
import type {SnapPlane} from 'components/customer/RoomPlanner/helpers/snapping/strategies/faceSnap';
import {PlaneFaces} from 'components/customer/RoomPlanner/helpers/movement/planeFaces';
import {
    clearOfWalls,
    keepInRoom,
} from 'components/customer/RoomPlanner/helpers/boundaries/roomClamp';
import {
    roomWallsOf,
    WallFacts,
} from 'components/customer/RoomPlanner/helpers/snapping/roomWalls';
import {
    SnapSettings,
    snapsAllowed,
} from 'components/customer/RoomPlanner/helpers/snapping/snapSettings';
import {checkCabinetOverlap} from 'components/customer/RoomPlanner/helpers/overlap';
import {productClamping} from 'components/customer/RoomPlanner/helpers/boundaries/clamping';
import {
    DescribedProduct,
    ProductRegistry,
} from 'components/customer/RoomPlanner/helpers/snapping/context';
import {
    ProductCache,
    ProductFacts,
} from 'components/customer/RoomPlanner/helpers/snapping/types';
import {Axis} from 'components/customer/RoomPlanner/helpers/axes';
import {getFacingRotation} from 'components/customer/RoomPlanner/helpers/calculations';
import {
    AxisAnchor,
    holdToAxis,
    onAxis,
    pixelsPerUnit,
    screenDirection,
    screenDistance,
} from 'components/customer/RoomPlanner/helpers/movement/axisHold';
import {
    blocks,
    buttTowards,
    FLUSH_TOLERANCE,
    Obstacle,
    overlappingOf,
    SNAP_TOLERANCE,
} from 'components/customer/RoomPlanner/helpers/movement/butting';
import {
    AlignmentBody,
    alignmentBodyOf,
    shiftBody,
    FaceAlignment,
    findFaceAlignment,
    HeldFaceAlignment,
    screenAlignmentRange,
    viewAxisOf,
} from 'components/customer/RoomPlanner/helpers/movement/faceAlignment';

/**
 * Where the room lets a product go: productClamping's answer.
 */
export type RoomClamp = ReturnType<typeof productClamping>;

/**
 * The room a drag happens in.
 */
export interface DragRoom {
    // Every product in the room, the dragged one included.
    products: THREE.Group[];
    // The camera the drag is seen through.
    camera: THREE.Camera;
    // The canvas size in pixels, which holding to an axis and face alignment
    // judge distances on. Without it nothing lines up, and a hold leaves the
    // product where it is.
    viewport?: THREE.Vector2 | null;
    wallGroup?: THREE.Object3D | null;
    // The top of the floor, which no product may be taken below. Without
    // one, height is not limited.
    floorTop?: number | null;
    /**
     * Whether a product may stand at a position: off the cut-out of an
     * L-shaped room, which the clamp's bounds span. Without it, anywhere the
     * clamp allows.
     */
    isWalkable?: (product: THREE.Group, position: THREE.Vector3) => boolean;
    /**
     * The top bar's snapping choices (CO-5856), read every move so a change
     * mid-drag applies at once. Without it, everything snaps and lines up.
     */
    settings?: () => SnapSettings;
    /**
     * Keeps the product in the room, turning it to face a wall it meets
     * unless told not to. May turn the product in place.
     */
    clampToRoom: (
        product: THREE.Group,
        target: THREE.Vector3,
        preventAutoRotation: boolean
    ) => RoomClamp;
}

/**
 * The pointer while Ctrl (Cmd on a Mac) holds the drag to one axis.
 */
export interface DragHold {
    // In pixels, as pointer events report it.
    pointer: THREE.Vector2;
    ray: THREE.Ray;
}

/**
 * Where one move puts the product.
 */
export interface DragMove {
    position: THREE.Vector3;
    // Where the pointer put it before snapping and the room had their say.
    target: THREE.Vector3;
    snapped: boolean;
    // The snap it took, for the guides (CO-5858): null whenever it didn't
    // snap.
    snap: Snap | null;
    // The axis the move is held to, if any.
    axis: Axis | null;
    // The face alignment it lined up with, if any.
    alignment: HeldFaceAlignment | null;
    // The dragged product's solid box and exterior faces where the move put
    // it, for drawing what lined up.
    body: AlignmentBody;
    // Whether something was in the way, so it stopped short of where the
    // pointer would put it, and where that was, for the ghost.
    heldBack: boolean;
    ghostAt: THREE.Vector3;
    // The products it really overlaps where it landed: none, unless the drag
    // started overlapping.
    overlaps: THREE.Group[];
    // Whether the move was refused because the product would stand off the
    // floor; it then stays where it was, and any turn the clamp gave it must
    // be undone.
    rejected: boolean;
    // The room clamp for this move.
    clamp: RoomClamp;
}

/**
 * The facts for one move: the other products' from the drag's registry,
 * since they hold still while it lasts, and the dragged product's measured
 * afresh, since it moves every time.
 */
class MoveRegistry implements ProductCache {
    private movingFacts?: ProductFacts;

    /**
     * @param {THREE.Group} moving The dragged product.
     * @param {ProductCache} others The drag's registry.
     */
    constructor(
        private readonly moving: THREE.Group,
        private readonly others: ProductCache
    ) {}

    /**
     * @param {THREE.Group} group A product in the room.
     * @return {ProductFacts} Its facts.
     */
    get(group: THREE.Group): ProductFacts {
        if (group !== this.moving) return this.others.get(group);

        this.movingFacts ??= new DescribedProduct(group);

        return this.movingFacts;
    }
}

// Stopped further short than this (0.005 mm) of where the pointer would put
// it, a product is held back by what is in its way.
const HELD_BACK = 0.001;

/**
 * A snap a move may take, and where it puts the product once the held axis
 * and height rules have had their say.
 */
interface SnapChoice {
    snap: Snap;
    position: THREE.Vector3;
}

// How far, in pixels, the pointer may move from where it last snapped and
// keep the snap once snapping stops finding it, so a hand resting at
// the edge of the snap range doesn't flick the product between snapped and
// free. Face alignment has the same margin between ALIGN_ENTER_PX and
// ALIGN_RELEASE_PX.
export const SNAP_KEEP_PX = 8;

// A move shorter than this (0.05 mm) is too small to tell which way the drag
// is going.
const MIN_DIRECTION_STEP = 0.01;

/**
 * @param {AlignmentBody} body A product's solid box and faces.
 * @return {THREE.Box3[]} Its separate solid boxes, or its box alone.
 */
const solidsOf = (body: AlignmentBody): readonly THREE.Box3[] =>
    body.solids ?? [body.bbox];

/**
 * @param {THREE.Group} product A product.
 * @return {boolean} True for products the room clamp must never turn to face
 * a wall: panels, and placeholders, Y-up models that a turn about z
 * would tip over.
 */
const neverTurns = ({userData}: THREE.Group) =>
    Boolean(
        userData.isPanelProduct ||
        userData.isManualPanel ||
        userData.isPlaceholder
    );

/**
 * A drag of one product, from mouse down to mouse up. Nothing it learns about
 * a snap is kept on the product, so every move starts from where the pointer
 * is and a snap lets go in any direction, once the pointer has moved
 * SNAP_KEEP_PX from where it snapped.
 */
export class DragSession {
    // The other products hold still for the whole drag, so each is measured
    // once.
    private readonly others = new ProductRegistry();
    // Its height, which only a hold to height changes, or, for a panel
    // or filler, a snap that lines its top or bottom up.
    private height: number;
    private readonly takesSnapHeight: boolean;
    // The lowest its origin may go, so its bottom stays on the floor, or
    // where it started if that was lower.
    private readonly lowest: number;
    private anchor: AxisAnchor | null = null;
    // The axis the hold is on, kept until the pointer clearly leaves it.
    private heldAxis: Axis | null = null;
    // The face alignment held last move, kept until pulled clearly away.
    private heldAlignment: HeldFaceAlignment | null = null;
    // The last snap taken: where the pointer put the product
    // then, and where the snap put it.
    // The corner a corner snap met last move, kept until the pointer pulls
    // clearly away (CO-5854).
    private heldCorner: HeldCorner | null = null;
    // The other products' faces by plane, and the walls', for the guides
    // (CO-5858): searched the first time a plane is asked about, since they
    // hold still.
    private planeFaces: PlaneFaces | null = null;
    private wallPlaneFaces: PlaneFaces | null = null;
    private heldSnap: {
        target: THREE.Vector3;
        position: THREE.Vector3;
        snap: Snap;
    } | null = null;
    // Where the pointer put the product last move, before any alignment,
    // and which way it has been moving since: the face leading that way is
    // the one that lines up.
    private lastTarget: THREE.Vector3 | null = null;
    private direction: THREE.Vector3 | null = null;
    // The dragged product's solid box and exterior faces relative to its
    // origin, and the turn they were measured at: measuring them scans every
    // component's vertices, so it is done again only when the product turns.
    private bodyFrame: {rotation: number; body: AlignmentBody} | null = null;
    // What the other products put in the way, and which of them it may line
    // up with: they hold still, so each list is made once per drag.
    private blockers: {obstacles: Obstacle[]; boxes: THREE.Box3[]} | null =
        null;
    private candidates: ProductFacts[] | null = null;

    /**
     * @return {WallFacts[]} The room's walls, to meet as products are, or
     * none.
     */
    private walls(): WallFacts[] {
        const walls = roomWallsOf(this.room.wallGroup);

        return walls ? [walls] : [];
    }

    /**
     * Where the room lets the product go on its way to a target, whatever
     * the room's shape. The room clamp may turn it to face a wall it reaches,
     * but where it goes is the walls' to say (keepInRoom): its bounds are a
     * rectangle, which can't follow an L-shaped room and held an upper on a
     * cut-out wall to that wall's span. A room with no walls is left to the
     * clamp.
     * @param {THREE.Vector3} target Where it is headed.
     * @param {DragHold} [hold] The pointer, while Ctrl is held: a held move
     * keeps the way the product faces.
     * @return {RoomClamp} Where it gets to, and the clamp's turn.
     */
    private roomClamp(target: THREE.Vector3, hold?: DragHold): RoomClamp {
        const {product, room} = this;
        const clamp = room.clampToRoom(
            product,
            target,
            neverTurns(product) || hold !== undefined
        );
        const [walls] = this.walls();
        if (!walls) return clamp;

        const clamped = keepInRoom(
            solidsOf(this.bodyAt(product.position)),
            product.position,
            target,
            walls.solidBBoxes
        );

        return {
            ...clamp,
            clamped,
            minX: -Infinity,
            maxX: Infinity,
            minZ: -Infinity,
            maxZ: Infinity,
            wasClamped: clamped.distanceTo(target) > HELD_BACK,
        };
    }

    /**
     * @param {THREE.Group} product The dragged product, at its mouse-down
     * position.
     * @param {DragRoom} room The room it is dragged in.
     */
    constructor(
        readonly product: THREE.Group,
        private readonly room: DragRoom
    ) {
        this.height = product.position.y;
        const {userData} = product;
        this.takesSnapHeight = Boolean(
            userData.isManualPanel ||
            userData.isPanelProduct ||
            userData.isVerminProduct ||
            userData.isFillerProduct
        );

        const bottom = new THREE.Box3().setFromObject(product).min.y;
        this.lowest =
            room.floorTop === undefined || room.floorTop === null
                ? -Infinity
                : Math.min(room.floorTop, bottom) - bottom + this.height;
    }

    /**
     * @param {THREE.Vector3} pointer Where the pointer puts the product on
     * the drag plane.
     * @param {DragHold} [hold] The pointer, while Ctrl holds the drag to one
     * axis.
     * @return {DragMove} Where the move puts it. The product itself only
     * turns, if the room clamp turns it to face a wall.
     */
    move(pointer: THREE.Vector3, hold?: DragHold): DragMove {
        const {product, room} = this;
        const pointed = this.targetFor(pointer, hold);
        const {axis} = pointed;
        // Searched from where the room lets it go, as the arrow keys' snaps
        // are, so a product pushed past a wall is found against it.
        const target = this.roomClamp(pointed.target, hold).clamped.clone();
        const registry = new MoveRegistry(product, this.others);
        const settings = room.settings?.();

        // A product rule's snap first, then a face. Nothing is held
        // between moves.
        const snaps = findSnaps(product, target, room.products, {
            camera: room.camera,
            overlappingCabinets: checkCabinetOverlap(
                product as CabinetMesh,
                target,
                room.products as CabinetMesh[],
                0,
                false,
                false,
                {products: registry}
            ),
            wallGroup: room.wallGroup,
            products: registry,
            axis: hold === undefined ? null : axis,
            body: this.bodyAt(target),
            viewport: room.viewport,
            heldCorner: this.heldCorner,
            ...snapsAllowed(settings),
        });
        const previousSnap = this.heldSnap;
        const previousCorner = this.heldCorner;
        const choices = this.snapChoices(snaps, target, hold, axis);

        this.trackDirection(target);

        const previousAlignment = this.heldAlignment;
        // Where the room let the pointer put it, before any line-up, which a
        // held snap is measured from.
        const reached = target.clone();
        // Lined up across a gap only when nothing snaps flush, and
        // while Align is on (CO-5856).
        let alignment =
            choices.length || settings?.align === false
                ? null
                : this.alignTo(target, hold, axis);
        this.heldAlignment = alignment?.held ?? null;
        if (alignment) target.add(alignment.offset);

        const clamp = this.roomClamp(target, hold);

        // A snap the room would move it off is not taken; the next is tried.
        const taken = this.snapFor(choices, reached, hold, axis, clamp);
        // A snap held after the pointer has left its range still wins over
        // lining up: the product stays where it snapped, so a line-up found
        // from the pointer would be drawn off from it (CO-5858).
        if (taken && alignment) {
            target.sub(alignment.offset);
            this.heldAlignment = null;
            alignment = null;
        }
        const snap = taken?.position ?? null;
        const position = snap ?? clamp.clamped.clone();

        // Pushed into something, it stops flush against it, and a ghost
        // shows where the pointer is. Where the pointer would put it is used
        // as it is when nothing is in the way there: a ghost on a clear spot,
        // past what held it back, is a place it can go. A snap lands at most
        // SNAP_TOLERANCE into its neighbour (snapChoices), and lands as it is.
        const ghostAt = position.clone();
        const {obstacles, boxes} = this.obstacles();
        let body = this.bodyAt(position);
        let overlaps = overlappingOf(solidsOf(body), obstacles);
        if (snap !== null && overlaps.length > 0) {
            const deep = obstacles.filter((obstacle) =>
                overlaps.includes(obstacle.product)
            );
            if (
                overlappingOf(solidsOf(body), deep, SNAP_TOLERANCE).length === 0
            ) {
                overlaps = [];
            }
        }
        if (overlaps.length > 0) {
            position.copy(
                buttTowards(
                    solidsOf(this.bodyAt(product.position)),
                    product.position,
                    position,
                    boxes
                )
            );
            body = this.bodyAt(position);
            overlaps = overlappingOf(solidsOf(body), obstacles);
        }

        // A pose off the floor is refused before anything about it is kept.
        if (room.isWalkable && !room.isWalkable(product, position)) {
            this.heldAlignment = previousAlignment;
            this.heldSnap = previousSnap;
            this.heldCorner = previousCorner;
            const stay = product.position.clone();
            const bodyThere = this.bodyAt(stay);

            return {
                position: stay,
                target,
                snapped: false,
                snap: null,
                axis,
                alignment: null,
                body: bodyThere,
                heldBack: false,
                ghostAt: stay.clone(),
                overlaps: overlappingOf(solidsOf(bodyThere), obstacles),
                rejected: true,
                clamp,
            };
        }

        const heldBack = position.distanceTo(ghostAt) > HELD_BACK;
        this.height = position.y;

        return {
            position,
            target,
            // A snap or alignment it was held back from didn't happen.
            snapped: snap !== null && !heldBack,
            snap: taken && !heldBack ? taken.snap : null,
            axis,
            alignment: heldBack ? null : (alignment?.held ?? null),
            body,
            heldBack,
            ghostAt,
            overlaps,
            rejected: false,
            clamp,
        };
    }

    /**
     * The other products' faces lying in a plane, for a guide to show the
     * faces in line with one it highlights (CO-5858). Found once per plane
     * per drag.
     * @param {SnapPlane} plane A plane.
     * @return {THREE.Box3[]} Their faces in it. Shared, so read only.
     */
    facesInPlane = (plane: SnapPlane): THREE.Box3[] => {
        this.planeFaces ??= new PlaneFaces(
            this.room.products
                .filter((other) => other !== this.product)
                .map((other) => this.others.get(other))
        );

        return this.planeFaces.in(plane);
    };

    /**
     * The shown walls' faces lying in a plane, as facesInPlane, apart so the
     * guides can draw them behind what stands in front of them.
     * @param {SnapPlane} plane A plane.
     * @return {THREE.Box3[]} Their faces in it. Shared, so read only.
     */
    wallFacesInPlane = (plane: SnapPlane): THREE.Box3[] => {
        this.wallPlaneFaces ??= new PlaneFaces(this.walls());

        return this.wallPlaneFaces.in(plane);
    };

    /**
     * @return {object} The products that get in the dragged one's way, by
     * their solid boxes, and all those boxes together, found once per drag.
     */
    private obstacles(): {obstacles: Obstacle[]; boxes: THREE.Box3[]} {
        if (!this.blockers) {
            const obstacles = this.room.products
                .filter((other) => blocks(this.product, other))
                .map((other) => ({
                    product: other,
                    bboxes: this.others.get(other).bodyBBoxes,
                }));
            this.blockers = {
                obstacles,
                boxes: obstacles.flatMap(({bboxes}) => bboxes),
            };
        }

        return this.blockers;
    }

    /**
     * Notes which way the drag is moving, from where the pointer put the
     * product before any alignment, so an alignment never steers itself. A
     * move too small to have a direction keeps the last one.
     * @param {THREE.Vector3} target Where the pointer puts the product.
     */
    private trackDirection(target: THREE.Vector3) {
        if (this.lastTarget) {
            const moved = target.clone().sub(this.lastTarget);
            if (moved.length() > MIN_DIRECTION_STEP) {
                this.direction = moved.normalize();
            }
        }
        this.lastTarget = target.clone();
    }

    /**
     * @param {THREE.Vector3} position Where the product's origin is.
     * @return {AlignmentBody} Its solid box and exterior faces there.
     */
    private bodyAt(position: THREE.Vector3): AlignmentBody {
        const rotation = getFacingRotation(this.product);
        if (this.bodyFrame?.rotation !== rotation) {
            const facts = new DescribedProduct(this.product);
            this.bodyFrame = {
                rotation,
                body: shiftBody(
                    // Its body, interior and legs included (CO-5961).
                    {...alignmentBodyOf(facts), solids: facts.bodyBBoxes},
                    this.product.position.clone().negate()
                ),
            };
        }

        return shiftBody(this.bodyFrame.body, position);
    }

    /**
     * @param {THREE.Vector3} target Where the pointer puts the product.
     * @param {DragHold} [hold] The pointer, while Ctrl is held.
     * @param {Axis | null} axis The held axis, if picked.
     * @return {FaceAlignment | null} The face alignment for this move. A
     * free drag lines up along the floor axes only, and a held one along its
     * axis only, height included.
     */
    private alignTo(
        target: THREE.Vector3,
        hold: DragHold | undefined,
        axis: Axis | null
    ): FaceAlignment | null {
        const {camera, viewport} = this.room;
        if (!viewport) return null;

        const axes: Axis[] =
            hold === undefined ? ['x', 'z'] : axis === null ? [] : [axis];
        if (axes.length === 0) return null;

        const moving = this.bodyAt(target);
        this.candidates ??= this.room.products
            .filter(
                (other) =>
                    other !== this.product && !other.userData.ignoreSnapping
            )
            .map((other) => this.others.get(other));
        // One measurement serves both the range and the direction.
        const perUnit = pixelsPerUnit(
            moving.bbox.getCenter(new THREE.Vector3()),
            camera,
            viewport
        );

        return findFaceAlignment(
            moving,
            [...this.candidates, ...this.walls()],
            axes,
            screenAlignmentRange(perUnit),
            this.heldAlignment,
            // Each axis's share weighed by how far it moves on screen. Seen at
            // a shallow angle, a floor drag's depth jitter is large in the
            // room but small on screen, so it must not decide which face
            // leads.
            this.direction && this.direction.clone().multiply(perUnit),
            viewAxisOf(camera.getWorldDirection(new THREE.Vector3()))
        );
    }

    /**
     * @param {THREE.Vector3} pointer Where the pointer puts the product on
     * the drag plane.
     * @param {DragHold} [hold] The pointer, while Ctrl is held.
     * @return {object} Where the pointer puts the product, at its own height
     * unless held to height and never below the floor, and the held axis.
     */
    private targetFor(
        pointer: THREE.Vector3,
        hold?: DragHold
    ): {target: THREE.Vector3; axis: Axis | null} {
        if (hold === undefined) {
            this.anchor = null;
            this.heldAxis = null;

            return {target: pointer.clone().setY(this.height), axis: null};
        }

        // Anchored where the product sits, snap included, so one flush
        // against a face stays flush while held.
        this.anchor ??= {
            position: this.product.position.clone(),
            pointer: hold.pointer.clone(),
            ray: hold.ray.clone(),
        };
        const anchor = this.anchor;
        const {camera, viewport} = this.room;
        const held = viewport
            ? holdToAxis(
                  anchor,
                  hold.pointer,
                  hold.ray,
                  (axis) =>
                      screenDirection(anchor.position, axis, camera, viewport),
                  this.heldAxis
              )
            : null;
        this.heldAxis = held?.axis ?? this.heldAxis;
        // Until the pointer picks an axis, the product stays put.
        const target = held?.position ?? anchor.position.clone();
        target.y = Math.max(target.y, this.lowest);

        return {target, axis: held?.axis ?? null};
    }

    /**
     * @param {Snap[]} snaps The snaps in reach this move, in the order to try
     * them (findSnaps).
     * @param {THREE.Vector3} target Where the pointer puts the product.
     * @param {DragHold} [hold] The pointer, while Ctrl is held.
     * @param {Axis | null} axis The held axis, if picked.
     * @return {SnapChoice[]} The snaps the move may take, in the same order,
     * with where each puts the product: those the held axis allows and
     * that leave it at most SNAP_TOLERANCE into a neighbour, or
     * FLUSH_TOLERANCE for a face or corner, so a face inset from the
     * carcase, as a door's edge is, can't take it in (CO-5961).
     */
    private snapChoices(
        snaps: Snap[],
        target: THREE.Vector3,
        hold: DragHold | undefined,
        axis: Axis | null
    ): SnapChoice[] {
        const {obstacles} = this.obstacles();

        return snaps
            .map((snap) => ({
                snap,
                position: this.snapAllowed(snap.position, target, hold, axis),
            }))
            .filter(
                (choice): choice is SnapChoice =>
                    choice.position !== null &&
                    overlappingOf(
                        solidsOf(this.bodyAt(choice.position)),
                        obstacles,
                        choice.snap.rule ? SNAP_TOLERANCE : FLUSH_TOLERANCE
                    ).length === 0
            );
    }

    /**
     * @param {SnapChoice[]} choices The snaps the move may take
     * (snapChoices).
     * @param {THREE.Vector3} target Where the pointer puts the product.
     * @param {DragHold} [hold] The pointer, while Ctrl is held.
     * @param {Axis | null} axis The held axis, if picked.
     * @param {RoomClamp} clamp Where the room lets it go.
     * @return {SnapChoice | null} The snap for this move and where it puts
     * the product: the first choice the room lets it stay at, or, when
     * none is in reach, the last one taken, where it was, while the pointer
     * is within SNAP_KEEP_PX of where that one was found.
     */
    private snapFor(
        choices: SnapChoice[],
        target: THREE.Vector3,
        hold: DragHold | undefined,
        axis: Axis | null,
        clamp: RoomClamp
    ): SnapChoice | null {
        const [walls] = this.walls();
        const inRoom = (position: THREE.Vector3) =>
            walls
                ? clearOfWalls(
                      solidsOf(this.bodyAt(position)),
                      walls.solidBBoxes
                  )
                : position.x >= clamp.minX - HELD_BACK &&
                  position.x <= clamp.maxX + HELD_BACK &&
                  position.z >= clamp.minZ - HELD_BACK &&
                  position.z <= clamp.maxZ + HELD_BACK;
        if (choices.length) {
            const found =
                choices.find(({position}) => inRoom(position)) ?? null;
            this.heldCorner = found?.snap.held ?? null;
            this.heldSnap = found && {
                target: target.clone(),
                position: found.position.clone(),
                snap: found.snap,
            };

            return found;
        }
        this.heldCorner = null;

        const held = this.heldSnap;
        const {camera, viewport} = this.room;
        const kept =
            held &&
            viewport &&
            screenDistance(target, held.target, camera, viewport) <=
                SNAP_KEEP_PX
                ? this.snapAllowed(held.position, target, hold, axis)
                : null;
        if (!kept || !held) {
            this.heldSnap = null;

            return null;
        }

        return {snap: held.snap, position: kept};
    }

    /**
     * @param {THREE.Vector3} snapped Where a snap puts the product.
     * @param {THREE.Vector3} target Where the pointer put it.
     * @param {DragHold} [hold] The pointer, while Ctrl is held.
     * @param {Axis | null} axis The held axis, if picked.
     * @return {THREE.Vector3 | null} The snap, at the product's height unless
     * held to height, or null when a held move may not take it: a snap may
     * only move a held product along the held axis.
     */
    private snapAllowed(
        snapped: THREE.Vector3,
        target: THREE.Vector3,
        hold: DragHold | undefined,
        axis: Axis | null
    ): THREE.Vector3 | null {
        const position = snapped.clone();
        // Panels and fillers take a snap's height (CO-5853 R3); everything
        // else keeps its own unless held to height.
        if (axis !== 'y' && !this.takesSnapHeight) position.y = target.y;
        position.y = Math.max(position.y, this.lowest);

        if (hold === undefined) return position;
        if (axis === null) return null;

        return onAxis(position.clone().sub(target), axis) ? position : null;
    }
}
